import { useEffect, useRef } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Mic, User } from "lucide-react";

export default function LiveTranscriptViewer({ segments = [], partial = "" }) {
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [segments]);

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-violet-50 to-white">
        <Mic className="w-4 h-4 text-violet-500" />
        <h3 className="text-sm font-semibold text-slate-700">Live Transcript</h3>
        {segments.length > 0 && (
          <Badge variant="secondary" className="ml-auto text-[10px]">
            {segments.length} segments
          </Badge>
        )}
      </div>
      <ScrollArea className="h-72 p-4">
        {segments.length === 0 && !partial && (
          <div className="flex flex-col items-center justify-center h-full text-slate-400">
            <Mic className="w-8 h-8 mb-2 opacity-50" />
            <p className="text-xs">Awaiting transcription...</p>
          </div>
        )}
        {segments.map((seg, i) => (
          <div key={i} className="mb-3 last:mb-0">
            <div className="flex items-start gap-2">
              {seg.speaker_id && (
                <div className="flex items-center gap-1 min-w-fit mt-0.5">
                  <User className="w-3 h-3 text-violet-400" />
                  <span className="text-[10px] font-medium text-violet-500 whitespace-nowrap">
                    {seg.speaker_id}
                  </span>
                </div>
              )}
              <div className="flex-1">
                <p className="text-sm text-slate-700 leading-relaxed">{seg.text}</p>
                {seg.translated_text && (
                  <p className="text-xs text-emerald-700 bg-emerald-50/50 border border-emerald-100/50 rounded-md p-1.5 mt-1 leading-relaxed italic flex flex-wrap items-center gap-1">
                    <span className="font-semibold not-italic text-[9px] uppercase text-emerald-600 bg-emerald-100 px-1.5 py-0.5 rounded border border-emerald-200">
                      {seg.translation_language ? seg.translation_language.toUpperCase() : "TRANSLATED"}
                    </span>
                    <span>{seg.translated_text}</span>
                  </p>
                )}
              </div>
            </div>
            {seg.start_time && (
              <span className="text-[10px] text-slate-400 ml-5">
                {Math.round(seg.start_time)}s
              </span>
            )}
          </div>
        ))}
        {partial && (
          <div className="text-sm text-slate-400 italic">{partial}...</div>
        )}
        <div ref={bottomRef} />
      </ScrollArea>
    </div>
  );
}
